import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { RouteTab } from './RouteTab'
import { Reveal } from '@/components/motion/Reveal'
import { Scramble } from '@/components/motion/SplitText'
import { useParallax } from '@/hooks/usePointerFx'
import { useDeckCard } from '@/hooks/useDeckCard'

/**
 * Slow-drifting colour fields behind a band. Decorative only, hidden from
 * assistive technology, and dropped on small screens where they would cost
 * paint time for something nobody sees behind the content.
 */
function BandAtmosphere({ tone }: { tone: BandTone }) {
  const drift = useParallax<HTMLDivElement>(0.06)

  const orbs: Record<BandTone, { className: string; style: string }[]> = {
    ice: [
      { className: 'bg-primary/25', style: 'h-[520px] w-[520px] -left-40 -top-32' },
      { className: 'bg-violet/20', style: 'h-[420px] w-[420px] right-0 top-1/3' },
    ],
    warm: [
      { className: 'bg-projected/20', style: 'h-[460px] w-[460px] -right-32 -top-24' },
      { className: 'bg-primary/15', style: 'h-[380px] w-[380px] left-1/4 bottom-0' },
    ],
    mint: [
      { className: 'bg-computes/20', style: 'h-[480px] w-[480px] -left-28 bottom-0' },
      { className: 'bg-primary/15', style: 'h-[400px] w-[400px] right-10 -top-20' },
    ],
    ink: [
      { className: 'bg-primary/40', style: 'h-[560px] w-[560px] -left-32 -top-24' },
      { className: 'bg-violet/30', style: 'h-[420px] w-[420px] right-0 bottom-0' },
    ],
    plain: [],
  }

  return (
    <div
      ref={drift}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 hidden lg:block"
    >
      {orbs[tone].map((orb, index) => (
        <span
          key={index}
          className={cn('anim-drift absolute rounded-full blur-[110px]', orb.className, orb.style)}
          style={{ animationDelay: `${index * -7}s` }}
        />
      ))}
    </div>
  )
}

/**
 * The recurring instrument motif: counter-rotating dashed rings, a slow radar
 * sweep, two orbiting nodes and a breathing core.
 *
 * Entirely decorative and hidden from assistive technology. Under
 * `prefers-reduced-motion` every animation stops and it settles into a static
 * dial, which is why each layer is legible on its own.
 */
function BandMark({ tone }: { tone: BandTone }) {
  const onInk = tone === 'ink'
  const stroke = onInk ? '#ffffff' : '#3b5bdb'
  const accent = onInk ? '#a5b4fc' : '#7c5cd6'

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute right-6 top-12 hidden h-56 w-56 xl:block 2xl:right-10 2xl:h-64 2xl:w-64"
    >
      <svg aria-hidden="true" viewBox="0 0 160 160" className="h-full w-full" fill="none">
        <defs>
          <linearGradient id="markRing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity={onInk ? 0.9 : 0.7} />
            <stop offset="100%" stopColor={accent} stopOpacity="0.15" />
          </linearGradient>
          <radialGradient id="markSweep">
            <stop offset="0%" stopColor={stroke} stopOpacity={onInk ? 0.3 : 0.22} />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Radar sweep */}
        <g className="mark-sweep">
          <path d="M80 80 L80 4 A76 76 0 0 1 148 52 Z" fill="url(#markSweep)" />
        </g>

        {/* Outer dashed ring, travelling dashes */}
        <g className="mark-spin">
          <circle
            cx="80"
            cy="80"
            r="74"
            stroke={stroke}
            strokeOpacity={onInk ? 0.35 : 0.22}
            strokeWidth="1"
            strokeDasharray="3 9"
            className="mark-dash"
          />
        </g>

        {/* Counter-rotating ring with long ticks */}
        <g className="mark-spin-rev">
          <circle
            cx="80"
            cy="80"
            r="58"
            stroke="url(#markRing)"
            strokeWidth="1.25"
            strokeDasharray="26 14"
            strokeLinecap="round"
          />
        </g>

        {/* Quiet mid ring */}
        <circle
          cx="80"
          cy="80"
          r="42"
          stroke={stroke}
          strokeOpacity={onInk ? 0.28 : 0.16}
          strokeWidth="1"
        />

        {/* Breathing inner ring */}
        <g className="mark-breathe">
          <circle
            cx="80"
            cy="80"
            r="24"
            stroke={accent}
            strokeOpacity={onInk ? 0.7 : 0.45}
            strokeWidth="1.25"
          />
        </g>

        {/* Orbiting nodes, opposite directions */}
        <g className="mark-orbit">
          <circle cx="80" cy="22" r="3" fill={stroke} fillOpacity={onInk ? 0.95 : 0.75} />
        </g>
        <g className="mark-orbit-rev">
          <circle cx="80" cy="122" r="2.2" fill={accent} fillOpacity={onInk ? 0.9 : 0.6} />
        </g>

        {/* Core */}
        <circle cx="80" cy="80" r="3" fill={stroke} />
      </svg>

      {/* Emanating pulses, staggered so one is always mid-flight. */}
      <span className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2">
        <span
          className={cn(
            'anim-pulse-ring absolute inset-0 rounded-full',
            onInk ? 'bg-white/25' : 'bg-primary/25',
          )}
        />
        <span
          className={cn(
            'anim-pulse-ring absolute inset-0 rounded-full',
            onInk ? 'bg-white/15' : 'bg-violet/20',
          )}
          style={{ animationDelay: '1.6s' }}
        />
      </span>
    </div>
  )
}

export function LayerChip({ layer, total }: { layer: number; total: number }) {
  const pad = (value: number) => String(value).padStart(2, '0')

  return (
    <Reveal variant="down" duration={600} className="flex justify-center">
      <p className="relative inline-flex items-center gap-2.5 overflow-hidden rounded-control border border-line bg-white/90 px-4 py-2 shadow-card backdrop-blur">
        <span
          aria-hidden="true"
          className="h-1.5 w-6 rounded-full bg-gradient-to-r from-primary via-projected to-computes"
        />
        <Scramble
          className="font-mono text-[10.5px] tracking-[0.16em] text-primary"
          text={`SIMULATION LAYER ${pad(layer)} / ${pad(total)}`}
        />
      </p>
    </Reveal>
  )
}

const TONES = {
  ice: 'bg-[radial-gradient(120%_100%_at_0%_0%,#eef2ff_0%,#f8fafc_45%,#fdfbf7_100%)]',
  warm: 'bg-[radial-gradient(120%_100%_at_100%_0%,#fdf6ee_0%,#fafbfd_50%,#f4f7ff_100%)]',
  mint: 'bg-[radial-gradient(120%_100%_at_0%_100%,#eefaf4_0%,#f7fafc_50%,#f3f5fe_100%)]',
  ink: 'bg-[linear-gradient(135deg,#151d38_0%,#1f2a55_55%,#2f3a78_100%)] text-white',
  plain: 'bg-white',
} as const

export type BandTone = keyof typeof TONES

export function Band({
  children,
  layer,
  total = 14,
  tone = 'ice',
  id,
  labelledBy,
  className,
  showMark = true,
  routeTab,
  /** Constrains the inner content to the reading rail instead of full width. */
  narrow = false,
}: {
  children: ReactNode
  layer?: number
  total?: number
  tone?: BandTone
  id?: string
  labelledBy?: string
  className?: string
  showMark?: boolean
  routeTab?: string
  narrow?: boolean
}) {
  // Each band is a card in the scroll deck; the transform is applied
  // imperatively by a single shared ticker.
  const deck = useDeckCard<HTMLElement>()

  return (
    <section
      ref={deck}
      id={id}
      aria-labelledby={labelledBy}
      className={cn(
        'relative isolate mx-auto w-full max-w-[1860px] overflow-hidden rounded-band shadow-band',
        'py-16 will-change-transform sm:py-20 lg:py-28',
        TONES[tone],
        className,
      )}
    >
      <BandAtmosphere tone={tone} />
      {showMark ? <BandMark tone={tone} /> : null}
      {routeTab ? <RouteTab label={routeTab} /> : null}
      {layer ? <LayerChip layer={layer} total={total} /> : null}
      <div
        className={cn('relative', narrow ? 'shell-narrow' : 'shell', layer ? 'mt-14 lg:mt-20' : '')}
      >
        {children}
      </div>
    </section>
  )
}

/**
 * Vertical stack of bands. The canvas and gutter come from the layout, so the
 * footer sits in the same rhythm as the page's own bands.
 */
export function BandStack({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-3 sm:gap-5">{children}</div>
}
